import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const VehicleImportDialog = ({ open, onOpenChange, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const ext = selectedFile.name.split('.').pop().toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(ext)) {
        toast.error('Please select a CSV or Excel file');
        return;
      }
      setFile(selectedFile);
      setPreview(null);
    }
  };

  const handlePreview = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/vehicles/import/preview`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setPreview(response.data);
      
      if (response.data.valid_rows === 0) {
        toast.error('No valid rows found in the file');
      } else {
        toast.success(`Preview loaded: ${response.data.valid_rows} valid, ${response.data.invalid_rows} invalid rows`);
      }
    } catch (error) {
      console.error('Error previewing import:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview import file');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!preview || preview.valid_rows === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setImporting(true);

    try {
      // Get only valid rows
      const validRows = preview.preview
        .filter(row => row.is_valid)
        .map(row => row.data);

      const response = await axios.post(`${API}/vehicles/import/confirm`, {
        rows: validRows,
      });

      if (response.data.created > 0) {
        toast.success(
          `Successfully imported ${response.data.created} vehicle(s)!`,
          {
            description: response.data.failed > 0 
              ? `${response.data.failed} row(s) failed to import`
              : undefined
          }
        );
        
        // Close dialog and refresh
        onOpenChange(false);
        onImportComplete();
        
        // Reset state
        setFile(null);
        setPreview(null);
      } else {
        toast.error('Failed to import any vehicles');
      }

      if (response.data.errors.length > 0) {
        console.error('Import errors:', response.data.errors);
      }
    } catch (error) {
      console.error('Error confirming import:', error);
      toast.error(error.response?.data?.detail || 'Failed to import vehicles');
    } finally {
      setImporting(false);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setPreview(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Vehicles</DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel file to import vehicles. Only valid rows will be imported.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Upload Section */}
          {!preview && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                <Upload className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                <div className="space-y-2">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-700 font-medium">
                      Choose a file
                    </span>
                    <input
                      id="file-upload"
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                  <p className="text-sm text-slate-600">or drag and drop</p>
                  <p className="text-xs text-slate-500">CSV or Excel files only</p>
                </div>
                {file && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-700">
                    <FileSpreadsheet className="h-4 w-4" />
                    <span>{file.name}</span>
                  </div>
                )}
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-3">
                    <div>
                      <p className="font-medium mb-2">Required Fields (4 mandatory):</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-red-500"></span>
                          <code className="bg-slate-100 px-2 py-0.5 rounded">stock_no</code>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-red-500"></span>
                          <code className="bg-slate-100 px-2 py-0.5 rounded">chassis_no</code>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-red-500"></span>
                          <code className="bg-slate-100 px-2 py-0.5 rounded">year</code>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-red-500"></span>
                          <code className="bg-slate-100 px-2 py-0.5 rounded">model</code>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="font-medium mb-2">Optional Fields (21 fields from UI sections):</p>
                      <div className="space-y-2">
                        <div className="border rounded-lg p-2 bg-blue-50">
                          <p className="text-xs font-semibold text-blue-900 mb-1">🚗 Vehicle Information (1 field)</p>
                          <div className="flex flex-wrap gap-1">
                            <code className="text-xs bg-white px-2 py-0.5 rounded">type</code>
                          </div>
                        </div>
                        
                        <div className="border rounded-lg p-2 bg-purple-50">
                          <p className="text-xs font-semibold text-purple-900 mb-1">📍 Auction Information (3 fields)</p>
                          <div className="flex flex-wrap gap-1">
                            <code className="text-xs bg-white px-2 py-0.5 rounded">auction_house</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">yard</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">purchase_date</code>
                          </div>
                        </div>
                        
                        <div className="border rounded-lg p-2 bg-green-50">
                          <p className="text-xs font-semibold text-green-900 mb-1">💰 Public Listing Details (17 fields)</p>
                          <div className="flex flex-wrap gap-1">
                            <code className="text-xs bg-white px-2 py-0.5 rounded">brand</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">public_price_usd</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">public_price_jpy</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">grade</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">auction_grade_entry</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">condition</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">mileage</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">fuel_type</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">engine_cc</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">transmission</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">body_type</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">color</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">doors</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">seats</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">drive_type</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">description</code>
                            <code className="text-xs bg-white px-2 py-0.5 rounded">features</code>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Download focused CSV template with only UI visible fields
                          // Required: stock_no, chassis_no, year, model
                          // Optional: Vehicle Info + Auction Info + Public Listing fields only
                          const template = `stock_no,chassis_no,year,model,type,brand,auction_house,yard,purchase_date,public_price_usd,public_price_jpy,grade,auction_grade_entry,condition,mileage,fuel_type,engine_cc,transmission,body_type,color,doors,seats,drive_type,description,features
TEST-001,ABC123XYZ456,2020,Corolla,Sedan,Toyota,USS Tokyo,Yard A,2024-01-10,15000,2200000,4.5,4B,Used,50000,Gasoline,1800,Automatic,Sedan,White,4,5,2WD,Well-maintained vehicle with low mileage. Full service history.,Navigation System|Backup Camera|Bluetooth|A/C|Power Steering
TEST-002,DEF789UVW012,2019,Civic,Sedan,Honda,JAA,Yard B,2024-01-11,14000,2100000,4.0,4A,Used,60000,Gasoline,1600,Manual,Sedan,Black,4,5,2WD,Sporty sedan in great condition. Manual transmission for driving enthusiasts.,Sunroof|Alloy Wheels|Cruise Control|ABS|Airbag
TEST-003,GHI345JKL678,2021,Camry,Sedan,Toyota,USS Osaka,Yard C,2024-01-12,18000,2600000,5.0,5,Used,45000,Hybrid,2000,Automatic,Sedan,Silver,4,5,2WD,Hybrid vehicle with excellent fuel economy. Premium grade.,Leather Seats|Premium Audio|Lane Assist|Navigation|Push Start`;
                          
                          const blob = new Blob([template], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'vehicle_import_template.csv';
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          window.URL.revokeObjectURL(url);
                          toast.success('Sample template with 25 fields downloaded!');
                        }}
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Download Sample Template (25 Fields)
                      </Button>
                    </div>
                    
                    <p className="text-xs text-slate-600">
                      File will be validated with preview before importing. All fields are optional except the 4 required ones.
                    </p>
                  </div>
                </AlertDescription>
              </Alert>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={handlePreview} disabled={!file || uploading}>
                  {uploading ? 'Loading Preview...' : 'Preview Import'}
                </Button>
              </div>
            </div>
          )}

          {/* Preview Section */}
          {preview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-slate-900">{preview.total_rows}</div>
                  <div className="text-sm text-slate-600">Total Rows</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-700">{preview.valid_rows}</div>
                  <div className="text-sm text-green-600">Valid Rows</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-red-700">{preview.invalid_rows}</div>
                  <div className="text-sm text-red-600">Invalid Rows</div>
                </div>
              </div>

              {/* Preview Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>#</TableHead>
                        <TableHead>Stock No</TableHead>
                        <TableHead>Chassis No</TableHead>
                        <TableHead>Year</TableHead>
                        <TableHead>Model</TableHead>
                        <TableHead>Brand</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Mileage</TableHead>
                        <TableHead>Cost (JPY)</TableHead>
                        <TableHead>Price (USD)</TableHead>
                        <TableHead>Validation</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.preview.map((row) => (
                        <TableRow key={row.row_number} className={!row.is_valid ? 'bg-red-50' : ''}>
                          <TableCell className="sticky left-0 bg-white">
                            {row.is_valid ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-600" />
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm sticky left-16 bg-white">{row.row_number}</TableCell>
                          <TableCell className="font-medium sticky left-32 bg-white">{row.data.stock_no}</TableCell>
                          <TableCell className="font-mono text-xs">{row.data.chassis_no}</TableCell>
                          <TableCell>{row.data.year}</TableCell>
                          <TableCell>{row.data.model}</TableCell>
                          <TableCell>{row.data.brand || '-'}</TableCell>
                          <TableCell>{row.data.type || '-'}</TableCell>
                          <TableCell>
                            {row.data.status ? (
                              <Badge variant="outline" className="text-xs">
                                {row.data.status}
                              </Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{row.data.mileage ? `${row.data.mileage} km` : '-'}</TableCell>
                          <TableCell>{row.data.purchase_cost_jpy ? `¥${Number(row.data.purchase_cost_jpy).toLocaleString()}` : '-'}</TableCell>
                          <TableCell>{row.data.public_price_usd ? `$${Number(row.data.public_price_usd).toLocaleString()}` : '-'}</TableCell>
                          <TableCell>
                            {row.errors.length > 0 ? (
                              <div className="space-y-1">
                                {row.errors.map((error, idx) => (
                                  <Badge key={`error-${idx}`} variant="destructive" className="text-xs">
                                    {error}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-sm text-slate-500">✓ Valid</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center">
                <Button variant="outline" onClick={() => setPreview(null)}>
                  Back
                </Button>
                <Button
                  onClick={handleConfirmImport}
                  disabled={preview.valid_rows === 0 || importing}
                >
                  {importing ? 'Importing...' : `Import ${preview.valid_rows} Vehicle(s)`}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VehicleImportDialog;
